import Script from 'next/script'
import { useMemo, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { cookieConsentSelectors } from '@/store/cookieConsent'
import { i18nL10nSelectors } from '@/store/i18nL10n'

const CookieConsentScript = ({ id, nonce, params }) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [initialLocale] = useState(params.locale)
  const appliedLocale = useRef(initialLocale)

  useEffect(() => {
    if (!isLoaded || appliedLocale.current === params.locale || typeof window.TGBConsent?.setLanguage !== 'function') {
      return
    }

    const updateLanguage = async () => {
      try {
        await window.TGBConsent.ready
        appliedLocale.current = await window.TGBConsent.setLanguage(params.locale)
      } catch (error) {
        // A newer language request supersedes the previous one during rapid switching.
        if (error?.name !== 'AbortError') {
          console.error('Failed to update cookie consent language:', error)
        }
      }
    }

    updateLanguage()
  }, [isLoaded, params.locale])

  return (
    <Script
      async
      src={`${params.cookieConsentScriptUrl}?key=${encodeURIComponent(params.cookieConsentClientKey)}&lang=${encodeURIComponent(initialLocale)}`}
      id={id}
      key={id}
      nonce={nonce}
      strategy="afterInteractive"
      onReady={() => setIsLoaded(true)}
    />
  )
}

const thirdPartyScripts = [
  {
    id: 'google-analytics',
    Component: ({ id, nonce, params }) => (
      <Script
        type={params.cookieConsentEnabled ? 'text/plain' : undefined}
        id={id}
        key={id}
        src={`https://www.googletagmanager.com/gtag/js?id=${params.googleAnalyticsId}`}
        data-category={params.cookieConsentEnabled ? 'analytics' : undefined}
        strategy="afterInteractive"
        nonce={nonce}
      />
    ),
    condition: params => !!params.googleAnalyticsId
  },
  {
    id: 'gtag-init',
    Component: ({ id, nonce, params }) => (
      <Script
        type={params.cookieConsentEnabled ? 'text/plain' : undefined}
        id={id}
        data-category={params.cookieConsentEnabled ? 'analytics' : undefined}
        strategy="afterInteractive"
        nonce={nonce}
      >
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${params.googleAnalyticsId}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
    ),
    condition: params => !!params.googleAnalyticsId
  },
  {
    id: 'cookie-consent',
    Component: CookieConsentScript,
    condition: params => !!params.cookieConsentScriptUrl && !!params.cookieConsentClientKey && !!params.locale
  }
]

export default function ThirdPartyScripts({
  nonce,
  googleAnalyticsId,
  cookieConsentScriptUrl,
  cookieConsentClientKey
}) {
  const shouldLoadCookieConsentScripts = useSelector(cookieConsentSelectors.selectShouldLoad)
  const locale = useSelector(i18nL10nSelectors.selectLocale)
  const cookieConsentEnabled = !!cookieConsentScriptUrl && !!cookieConsentClientKey
  const getParamsByScriptId = useCallback(
    scriptId => {
      switch (scriptId) {
        case 'gtag-init':
        case 'google-analytics':
          return { googleAnalyticsId, cookieConsentEnabled }
        case 'cookie-consent':
          return { cookieConsentScriptUrl, cookieConsentClientKey, locale }
        default:
          return {}
      }
    },
    [cookieConsentClientKey, cookieConsentEnabled, cookieConsentScriptUrl, googleAnalyticsId, locale]
  )

  const filteredScripts = useMemo(
    () =>
      thirdPartyScripts.filter(script => {
        if (script.id === 'cookie-consent' && !shouldLoadCookieConsentScripts) {
          return false
        }

        if (typeof script.condition === 'function') {
          return script.condition(getParamsByScriptId(script.id))
        }
        return script.condition
      }),
    [getParamsByScriptId, shouldLoadCookieConsentScripts]
  )

  if (!filteredScripts.length) return null

  return (
    <>
      {filteredScripts.map(script => (
        <script.Component key={script.id} id={script.id} nonce={nonce} params={getParamsByScriptId(script.id)} />
      ))}
    </>
  )
}
